/**
 * Compact details renderer for invoice / order-summary line items.
 *
 * Given the snapshot dicts a saved POS item carries
 * (`jacketStyling` / `pantStyling` / `vestStyling`,
 *  `jacketStartingSpecs` + `jacketAdjustments` etc.), renders:
 *   • A "FINISHED MEASUREMENTS" sub-row with `starting + adjustment` totals,
 *     skipping fields where starting wasn't entered.
 *   • A "STYLE DETAILS" sub-row with the meaningful picks (skips default
 *     "Normal" / "Standard" / "None" values to keep the invoice readable).
 *
 * Designed for the printed-receipt section of the invoice — black text on
 * white, tight 11-12px typography.
 */

// Field display labels — only fields we want to surface on the printed
// invoice. Order matters: this is the rendering sequence.
const JACKET_MEASUREMENTS = [
  ["chest",  "Chest"],
  ["stomach","Stomach"],
  ["seat",   "Seat"],
  ["shoulder","Shoulder"],
  ["bicep",  "Bicep"],
  ["sleeve_length", "Sleeve Length"],
  ["right_sleeve_length","R-Sleeve Length"],
  ["left_sleeve_length","L-Sleeve Length"],
  ["back_length","Back Length"],
  ["front_length","Front Length"],
  ["right_cuff","R-Cuff"],
  ["left_cuff","L-Cuff"],
  ["cuff",   "Cuff"],
];

const PANT_MEASUREMENTS = [
  ["waist","Waist"],
  ["seat", "Seat"],
  ["urise","Rise"],
  ["thigh","Thigh"],
  ["knee","Knee"],
  ["bottom","Bottom"],
  ["right_pant_length","R-Pant Length"],
  ["left_pant_length","L-Pant Length"],
  ["pant_length","Pant Length"],
  ["inseam","Inseam"],
  ["outseam","Outseam"],
];

const VEST_MEASUREMENTS = [
  ["chest","Chest"],
  ["stomach","Stomach"],
  ["back_length","Back Length"],
  ["front_length","Front Length"],
  ["bottom","Bottom"],
];

const JACKET_STYLE_FIELDS = [
  ["canvas_construction","Canvas"],
  ["body_style","Fit"],
  ["front_closure","Closure"],
  ["lapel_type","Lapel"],
  ["lapel_width","Lapel W."],
  ["waist_pocket_type","Waist Pkt"],
  ["chest_pocket","Chest Pkt"],
  ["ticket_pocket","Ticket Pkt"],
  ["vent","Vent"],
  ["sleeve_buttonholes","Sleeve Btns"],
  ["lining_style","Lining"],
  ["lining_color","Body Lining"],
  ["sleeve_lining_color","Sleeve Lining"],
  ["inner_facing_piping","Piping"],
  ["button_style","Buttons"],
];

const PANT_STYLE_FIELDS = [
  ["body_style","Fit"],
  ["front_style","Front"],
  ["front_pocket","Front Pkt"],
  ["back_pocket_style","Back Pkt"],
  ["bottom_finishing","Bottom"],
  ["waistband_style","Waistband"],
  ["pant_lining_color","Lining"],
  ["pant_front_button_code","Buttons"],
];

const VEST_STYLE_FIELDS = [
  ["vest_lapel","Lapel"],
  ["vest_front_closure","Closure"],
  ["vest_pocket_type","Pocket"],
  ["vest_back","Back"],
  ["vest_lining_color","Lining"],
];

// Defaults we skip so the invoice doesn't list every untouched field.
const SKIP_VALUES = new Set([
  "", "Normal", "Standard", "None", "Default", "default", "—",
]);

const isMeaningful = (v) => {
  if (v === null || v === undefined) return false;
  const s = String(v).trim();
  if (!s) return false;
  return !SKIP_VALUES.has(s);
};

/**
 * Compute a single measurement's final value from starting + adjustment.
 * Returns the formatted string or null if no starting value was entered.
 */
function finalMeasurement(starting, adjustment) {
  const s = parseFloat(starting);
  if (!Number.isFinite(s)) return null;
  const a = parseFloat(adjustment) || 0;
  const total = s + a;
  // Show one decimal unless it's clean — keeps the receipt tight.
  return Number.isInteger(total * 1) ? total.toString() : total.toFixed(2);
}

function MeasurementGrid({ label, fields, starting = {}, adjustments = {}, testId }) {
  const rows = [];
  for (const [key, prettyLabel] of fields) {
    const v = finalMeasurement(starting[key], adjustments[key]);
    if (v === null) continue;
    rows.push({ key, label: prettyLabel, value: v });
  }
  if (rows.length === 0) return null;
  return (
    <div className="mt-1" data-testid={testId}>
      <p className="text-[9px] uppercase tracking-wider text-gray-500 font-semibold">{label}</p>
      <div className="grid grid-cols-3 gap-x-3 gap-y-0.5 mt-0.5">
        {rows.map((r) => (
          <div key={r.key} className="text-[11px] text-gray-700 flex justify-between">
            <span className="text-gray-500">{r.label}</span>
            <span className="font-medium text-gray-900">{r.value}&Prime;</span>
          </div>
        ))}
      </div>
    </div>
  );
}

function StyleGrid({ label, fields, styling = {}, testId }) {
  const rows = [];
  for (const [key, prettyLabel] of fields) {
    const v = styling[key];
    if (!isMeaningful(v)) continue;
    rows.push({ key, label: prettyLabel, value: String(v) });
  }
  if (rows.length === 0) return null;
  return (
    <div className="mt-1" data-testid={testId}>
      <p className="text-[9px] uppercase tracking-wider text-gray-500 font-semibold">{label}</p>
      <div className="grid grid-cols-2 gap-x-3 gap-y-0.5 mt-0.5">
        {rows.map((r) => (
          <div key={r.key} className="text-[11px] text-gray-700 flex gap-2">
            <span className="text-gray-500 shrink-0">{r.label}:</span>
            <span className="font-medium text-gray-900 truncate" title={r.value}>{r.value}</span>
          </div>
        ))}
      </div>
    </div>
  );
}

/**
 * Public entry point — renders the full block (measurements + styles) for
 * each garment present on the item. Pass `item` (a saved-items snapshot)
 * or pass the current-step state inline.
 */
export default function InvoiceItemDetails({
  // Jacket
  includeJacket = true,
  jacketStartingSpecs = {},
  jacketAdjustments = {},
  jacketStyling = {},
  // Trouser
  includeTrouser = true,
  trouserStartingSpecs = {},
  trouserAdjustments = {},
  pantStyling = {},
  // Vest
  includeVest = false,
  vestStartingSpecs = {},
  vestAdjustments = {},
  vestStyling = {},
  // Optional shirt placeholder for future expansion
  // includeShirt = false,
  testIdPrefix = "invoice-item-details",
}) {
  const blocks = [];
  if (includeJacket) {
    const hasContent =
      Object.values(jacketStartingSpecs || {}).some(isMeaningful) ||
      Object.values(jacketStyling || {}).some(isMeaningful);
    if (hasContent) {
      blocks.push(
        <div key="jacket" className="mt-2 pt-2 border-t border-gray-100" data-testid={`${testIdPrefix}-jacket`}>
          <p className="text-[10px] font-bold uppercase tracking-wider text-amber-700">Jacket</p>
          <MeasurementGrid
            label="Finished Measurements"
            fields={JACKET_MEASUREMENTS}
            starting={jacketStartingSpecs}
            adjustments={jacketAdjustments}
            testId={`${testIdPrefix}-jacket-measurements`}
          />
          <StyleGrid
            label="Style Details"
            fields={JACKET_STYLE_FIELDS}
            styling={jacketStyling}
            testId={`${testIdPrefix}-jacket-styles`}
          />
        </div>,
      );
    }
  }
  if (includeTrouser) {
    const hasContent =
      Object.values(trouserStartingSpecs || {}).some(isMeaningful) ||
      Object.values(pantStyling || {}).some(isMeaningful);
    if (hasContent) {
      blocks.push(
        <div key="trouser" className="mt-2 pt-2 border-t border-gray-100" data-testid={`${testIdPrefix}-trouser`}>
          <p className="text-[10px] font-bold uppercase tracking-wider text-blue-700">Trouser</p>
          <MeasurementGrid
            label="Finished Measurements"
            fields={PANT_MEASUREMENTS}
            starting={trouserStartingSpecs}
            adjustments={trouserAdjustments}
            testId={`${testIdPrefix}-trouser-measurements`}
          />
          <StyleGrid
            label="Style Details"
            fields={PANT_STYLE_FIELDS}
            styling={pantStyling}
            testId={`${testIdPrefix}-trouser-styles`}
          />
        </div>,
      );
    }
  }
  if (includeVest) {
    const hasContent =
      Object.values(vestStartingSpecs || {}).some(isMeaningful) ||
      Object.values(vestStyling || {}).some(isMeaningful);
    if (hasContent) {
      blocks.push(
        <div key="vest" className="mt-2 pt-2 border-t border-gray-100" data-testid={`${testIdPrefix}-vest`}>
          <p className="text-[10px] font-bold uppercase tracking-wider text-purple-700">Vest</p>
          <MeasurementGrid
            label="Finished Measurements"
            fields={VEST_MEASUREMENTS}
            starting={vestStartingSpecs}
            adjustments={vestAdjustments}
            testId={`${testIdPrefix}-vest-measurements`}
          />
          <StyleGrid
            label="Style Details"
            fields={VEST_STYLE_FIELDS}
            styling={vestStyling}
            testId={`${testIdPrefix}-vest-styles`}
          />
        </div>,
      );
    }
  }
  if (blocks.length === 0) return null;
  return <div className="text-gray-900">{blocks}</div>;
}
